import type { SubscriptionPlanId } from 'api/types';

export type PlanPitch = {
  targetTier: SubscriptionPlanId;
  planName: string;
  headline: string;
  bullets: string[];
  ctaLabel: string;
};

const PITCH_BY_TARGET: Partial<Record<SubscriptionPlanId, PlanPitch>> = {
  launch: {
    targetTier: 'launch',
    planName: 'Launch',
    headline: 'Move to a Launch plan and save on every verification',
    bullets: [
      'Pre-paid credits give you the lowest per-verification rate',
      'Pay-as-you-go simplicity — no commitment, cancel anytime',
      'Email support and developer docs included',
    ],
    ctaLabel: 'Upgrade',
  },
  growth: {
    targetTier: 'growth',
    planName: 'Growth',
    headline: "You're outgrowing Launch — Growth was built for this",
    bullets: [
      '8% volume discount on every verification',
      '30-day onboarding support to get the most out of Truv',
      'Higher pre-paid credit allowance per cycle',
    ],
    ctaLabel: 'Upgrade',
  },
  professional: {
    targetTier: 'professional',
    planName: 'Professional',
    headline: 'Scale without the surprises — meet Professional',
    bullets: [
      '15% volume discount unlocks at this tier',
      'Dedicated CSM and custom team training',
      'POS / LOS configuration support included',
    ],
    ctaLabel: 'Upgrade',
  },
  professional_plus: {
    targetTier: 'professional_plus',
    planName: 'Professional Plus',
    headline: 'Lock in our best rates — Professional Plus',
    bullets: [
      '25% volume discount — our lowest per-verification rate',
      'Premium support with priority response',
      'Custom MSA and enterprise security review',
    ],
    ctaLabel: 'Upgrade',
  },
};

const NEXT_TIER: Record<string, SubscriptionPlanId> = {
  one_off: 'launch',
  launch: 'growth',
  growth: 'professional',
  professional: 'professional_plus',
};

/**
 * Resolve the next-tier pitch for a user's current plan. Returns null when:
 *   - the user is at the top of the ladder (`professional_plus`), or
 *   - the catalog hasn't shipped the next tier yet (e.g., `professional_plus`
 *     not in {@link availablePlans}). Pass {@link availablePlans} to gate the
 *     pitch on what the FE can actually display in the upgrade flow.
 */
export const nextTierPitch = (
  currentPlan: SubscriptionPlanId | null | undefined,
  availablePlans?: SubscriptionPlanId[],
): PlanPitch | null => {
  const sourceKey = currentPlan ?? 'one_off';
  const targetTier = NEXT_TIER[sourceKey];
  if (!targetTier) {
    return null;
  }
  if (availablePlans && !availablePlans.includes(targetTier)) {
    return null;
  }
  return PITCH_BY_TARGET[targetTier] ?? null;
};
